import { useMemo } from 'react';
import { Divider } from '@mui/material';
import fuzzysort from 'fuzzysort';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import type { TranscriptionResponseDTO } from '@klangio/ai2notes-ts-client';
import { SortOption } from './Songbook';
import SongbookItem from '../SongbookItem/SongbookItem';
import VirtualizedListWrapper from './VirtualizedListWrapper';

const useStyles = makeStyles()((theme) => ({
  emptyContainer: {
    gap: '1em',
    marginTop: '2rem',
    padding: '2rem',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    colorScheme: 'dark',
    position: 'relative',
    height: 'calc(100vh - 340px)',
    overflow: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    [theme.breakpoints.down('md')]: {
      height: '100vh',
      overflow: 'hidden',
    },
  },
}));

export interface SonglistProps {
  transcriptions: TranscriptionResponseDTO[];
  searchString?: string;
  sortOption?: SortOption;
  sortAscending?: boolean;
}

const Songlist = ({
  transcriptions,
  sortAscending,
  sortOption,
  searchString,
}: SonglistProps): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { data, searchResults } = useMemo(() => {
    let displayData = [...transcriptions];
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let searchResults: Fuzzysort.KeyResults<TranscriptionResponseDTO> | undefined;

    if (searchString && searchString.trim() !== '') {
      searchResults = fuzzysort.go(searchString, displayData, { key: 'title' });
      displayData = searchResults.map((res) => res.obj);
    } else {
      switch (sortOption) {
        case SortOption.ByTitle:
          displayData.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case SortOption.ByCreationDate:
        default:
          displayData.sort((a, b) => +new Date(a.creationDate) - +new Date(b.creationDate));
          break;
      }

      if (!sortAscending) {
        displayData.reverse();
      }
    }

    return { data: displayData, searchResults };
  }, [transcriptions, sortOption, sortAscending, searchString]);

  if (data.length === 0) {
    return (
      <div className={classes.emptyContainer}>
        <span>{t('home.songbook.no_songs_found')}</span>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <VirtualizedListWrapper
        items={data}
        height="100%"
        renderItem={(transcription: TranscriptionResponseDTO, i: number) => (
          <>
            <SongbookItem
              key={transcription.id}
              transcription={transcription}
              searchResult={searchResults?.[i]}
            />
            {(!searchResults || i < data.length - 1) && <Divider sx={{ margin: '0 9px' }} />}
          </>
        )}
      />
    </div>
  );
};

export default Songlist;
